import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle, RecipeDetailModal } from '@/components/common';
import {
  Search,
  Bell,
  FileText,
  AlertCircle,
  Loader2,
  Eye,
  Calendar,
  FlaskConical,
  Stethoscope,
  ClipboardList,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { getPatientByUserId } from '@/services/index';
import { getPatientDocuments, getPatientRenewalRequests, requestPrescriptionRenewal } from '@/services/documents';
import type { DocumentDTO, RenewalRequestDTO } from '@/services/documents';
import { clinicalDocumentLabel, documentDoctorName } from '@/lib/clinicalDocuments';
import { formatAppointmentReasonForDisplay } from '@/lib/appointmentReason';
import { documentStatusLabel } from '@/lib/documentStatus';
import {
  getPrescriptionValidity,
  isPrescriptionDocumentType,
  urgencyBadgeClass,
} from '@/lib/prescriptionValidity';

export default function MeusDocumentosPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading, logout } = useAuth();
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [documents, setDocuments] = useState<DocumentDTO[]>([]);
  const [renewalRequests, setRenewalRequests] = useState<RenewalRequestDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<DocumentDTO | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [requestingRenewalId, setRequestingRenewalId] = useState<string | null>(null);
  const [highlightedDocId, setHighlightedDocId] = useState<string | null>(null);

  const deepLinkDocumentId = searchParams.get('documentId');

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (!isLoading && user && user.role !== UserRole.PATIENT) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Load documents
  const loadDocuments = async () => {
    if (!user || user.role !== UserRole.PATIENT) return;

    setIsLoadingDocs(true);
    setError(null);

    try {
      // Get patient profile
      const patientResponse = await getPatientByUserId(user.id);
      if (!patientResponse.data) {
        setError('Perfil de paciente não encontrado');
        return;
      }

      // Preferir userId (appointments.patientId); backend também aceita patients.id
      const patientUserId =
        patientResponse.data.userId || user.id || patientResponse.data.id;
      const docsResponse = await getPatientDocuments(patientUserId);
      
      let renewals: any[] = [];
      try {
        const renReqs = await getPatientRenewalRequests();
        if (renReqs.success && renReqs.data) {
          renewals = renReqs.data;
        }
      } catch (err) {
        console.error('Error fetching renewals', err);
      }
      setRenewalRequests(renewals);

      // Ensure docsResponse.data is always an array
      const documentsData = Array.isArray(docsResponse.data) ? docsResponse.data : [];
      setDocuments(documentsData);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Erro ao carregar seus documentos');
      setDocuments([]);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [user]);

  // Deep link: abrir documento alvo vindo do dashboard (?documentId=)
  useEffect(() => {
    if (!deepLinkDocumentId || isLoadingDocs || documents.length === 0) return;

    const target = documents.find((d) => d.id === deepLinkDocumentId);
    if (!target) return;

    setSelectedDoc(target);
    setIsDetailModalOpen(true);
    setHighlightedDocId(target.id);

    // Remove o query param após consumir, sem perder histórico de navegação útil
    const next = new URLSearchParams(searchParams);
    if (!next.has('documentId')) return;
    next.delete('documentId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consumir documentId uma vez ao carregar lista
  }, [deepLinkDocumentId, documents, isLoadingDocs]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleViewDoc = (doc: DocumentDTO) => {
    setSelectedDoc(doc);
    setIsDetailModalOpen(true);
    setHighlightedDocId(doc.id);
  };

  const handleRequestRenewal = async (docId: string) => {
    setRequestingRenewalId(docId);
    try {
      const res = await requestPrescriptionRenewal(docId);
      if (res.success) {
        toast.success('Solicitação de renovação enviada com sucesso!');
        loadDocuments(); // Reload to get updated renewal requests
      } else {
        toast.error(res.error || 'Erro ao solicitar renovação');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erro de conexão ao solicitar renovação');
    } finally {
      setRequestingRenewalId(null);
    }
  };

  const filteredDocuments = (documents || []).filter((doc) => {
    const term = searchTerm.toLowerCase();
    const doctorName = documentDoctorName(doc).toLowerCase();
    const specialty = (doc.appointment?.doctor?.specialty || '').toLowerCase();
    const typeLabel = clinicalDocumentLabel(doc.documentType || '').toLowerCase();
    return (
      doctorName.includes(term) ||
      specialty.includes(term) ||
      typeLabel.includes(term) ||
      doc.documentType?.toLowerCase().includes(term)
    );
  });

  const getDocTypeInfo = (type: string) => {
    const label = clinicalDocumentLabel(type);
    const t = type.toLowerCase();
    if (t.includes('prescription') || t.includes('receita') || t === 'recipe') {
      return { label, icon: FileText, color: 'text-teal-600', bgColor: 'bg-teal-50 dark:bg-teal-900/20' };
    }
    if (t.includes('exam') || t.includes('exame') || t.includes('report')) {
      return { label, icon: FlaskConical, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/20' };
    }
    if (t.includes('diagnosis') || t.includes('diagnóstico')) {
      return { label, icon: Stethoscope, color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-900/20' };
    }
    if (t.includes('certificate') || t.includes('atestado')) {
      return { label, icon: ClipboardList, color: 'text-indigo-600', bgColor: 'bg-indigo-50 dark:bg-indigo-900/20' };
    }
    return { label, icon: FileText, color: 'text-gray-600', bgColor: 'bg-gray-50 dark:bg-gray-900/20' };
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      {/* Sidebar */}
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Documentos Médicos
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Visualize suas receitas, exames, diagnósticos e atestados
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden md:block w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por médico, especialidade ou tipo..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-300 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
              />
            </div>

            <button className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            <ThemeToggle />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-8 bg-gray-50 dark:bg-neutral-950">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoadingDocs ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin mr-2" />
              <p className="text-gray-600 dark:text-gray-400">
                Carregando documentos...
              </p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            /* Empty State */
            <div className="bg-white dark:bg-neutral-900 rounded-lg p-12 text-center border border-gray-200 dark:border-neutral-800">
              <ClipboardList className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {documents.length === 0
                  ? 'Nenhum documento disponível'
                  : 'Nenhum documento encontrado'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {documents.length === 0
                  ? 'Você ainda não possui documentos médicos registrados.'
                  : 'Nenhum documento corresponde aos seus critérios de busca.'}
              </p>
            </div>
          ) : (
            /* Documents List */
            <div className="grid gap-4">
              {filteredDocuments.map(doc => {
                const docDate = new Date(doc.createdAt);
                const doctorName = documentDoctorName(doc);
                const specialty = doc.appointment?.doctor?.specialty || 'Especialidade';
                let typeInfo = getDocTypeInfo(doc.documentType || '');
                const Icon = typeInfo.icon;
                const statusLabel = documentStatusLabel(doc);
                
                // Checar vencimento para receitas
                const isPrescription = isPrescriptionDocumentType(doc.documentType);
                const validity = isPrescription
                  ? getPrescriptionValidity(doc.expiresAt)
                  : null;
                const isExpired = validity?.urgency === 'expired';
                
                // Procurar se já existe um request
                const renewalRequest = renewalRequests.find(r => r.prescriptionId === doc.id);
                const isRenewalPending = renewalRequest && ['REQUESTED', 'IN_QUEUE', 'IN_REVIEW'].includes(renewalRequest.status);

                let cardClass = "bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-800";
                if (isExpired) {
                  cardClass = "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/50";
                  typeInfo = { ...typeInfo, color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/30' };
                } else if (validity?.urgency === 'critical' || validity?.urgency === 'warning') {
                  cardClass =
                    'bg-amber-50/70 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40';
                }
                if (highlightedDocId === doc.id && !isExpired) {
                  cardClass =
                    'bg-teal-50/60 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700 ring-1 ring-teal-200 dark:ring-teal-800';
                }

                return (
                  <div
                    key={doc.id}
                    id={`document-${doc.id}`}
                    className={`rounded-lg p-6 border hover:shadow-md transition-shadow ${cardClass}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`p-2 rounded-lg ${typeInfo.bgColor} ${typeInfo.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                              {typeInfo.label}
                              {statusLabel && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800 uppercase tracking-wider dark:bg-teal-900/40 dark:text-teal-200">
                                  {statusLabel}
                                </span>
                              )}
                              {isExpired && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 uppercase tracking-wider">
                                  Vencida
                                </span>
                              )}
                              {validity && !isExpired && validity.urgency !== 'unknown' && (
                                <span
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${urgencyBadgeClass(validity.urgency)}`}
                                >
                                  {validity.label}
                                </span>
                              )}
                              {isRenewalPending && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 uppercase tracking-wider">
                                  Renovação em Análise
                                </span>
                              )}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {doctorName}
                              </span>
                              <span className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {specialty}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            Data: {docDate.toLocaleDateString('pt-BR')}
                          </div>
                          {validity?.expiresAt && (
                             <div className={`flex items-center gap-2 ${isExpired ? 'text-red-600 font-medium' : ''}`}>
                              <Calendar className={`w-4 h-4 ${isExpired ? 'text-red-500' : 'text-gray-400'}`} />
                              Vencimento: {validity.expiresAt.toLocaleDateString('pt-BR')}
                              {validity.daysRemaining !== null && !isExpired && (
                                <span className="text-xs font-medium">
                                  ({validity.label})
                                </span>
                              )}
                            </div>
                          )}
                          {doc.appointment?.date && (
                             <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              Consulta: {new Date(doc.appointment.date).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>

                        {doc.appointment?.reason && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong>Motivo da Consulta:</strong>{' '}
                            {formatAppointmentReasonForDisplay(doc.appointment.reason)}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        {isExpired && !isRenewalPending && (
                           <button
                           onClick={() => handleRequestRenewal(doc.id)}
                           disabled={requestingRenewalId === doc.id}
                           className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                         >
                           {requestingRenewalId === doc.id ? (
                             <Loader2 className="w-4 h-4 animate-spin" />
                           ) : (
                             <RefreshCw className="w-4 h-4" />
                           )}
                           Renovar Receita
                         </button>
                        )}
                        <button
                          onClick={() => handleViewDoc(doc)}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-500 transition-colors text-sm font-medium"
                        >
                          <Eye className="w-4 h-4" />
                          Ver Documento
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>

        <RecipeDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedDoc(null);
          }}
          recipe={selectedDoc}
        />
      </div>
    </div>
  );
}

